import * as React from 'react';
import LocalDiv from '../../../../localization/LocalDiv';
import { FLEX_COLUMN } from '../../../../layout/flexBox';
import Spacer from '../../../../layout/Spacer';
import Msg from '../../../../localization/Msg';
import { Str } from '../../../../localization/translationKeys';
import { useAppState } from '../../../../core/redux/hooks';
import zfLogo from '../../../../assets/logo_zf.png';
import hrdqLogo from '../../../../assets/logo_hrdq.gif';
import talentSmartLogo from '../../../../assets/logo_talentsmart.png';
import truScoreLogo from '../../../../assets/logo.svg';
import { css } from '@emotion/core';
import { useBrowser } from '../../../../language/useBrowser';
import { addImgEncoding } from '../../../../language/module';
import { useRoutes } from '../../../../navigation/routes';
import { useCollaborationProjIds } from '../hooks';
import CollaboratorAppActions from './CollaboratorAppActions';
import { COLORS } from '../../../../globalStyle/colors';
import CollaboratorProjectSteps from './CollaboratorProjectSteps';


const styles = {
  root:       css`
    align-items: stretch;
    padding: 0 10px 15px;
    height: 100%;
    box-sizing: border-box;

    overflow-y: scroll;
    -ms-overflow-style: none; // IE-specific
    scrollbar-width: none; // Firefox-specific

    ::-webkit-scrollbar {
      width: 0;
      background: transparent;
    }
  `,
  logo:       css`
    width: 184px;
    margin: 20px auto; 
    padding: 0;
  `,
  spacer:     css`
    flex-basis: 30px;
    flex-shrink: 0;
  `,
  appActions: css`
    margin-top: auto;
    margin-bottom: 15px;
  `,
  footer:     css`
    font-size: 11.5px;
    color: ${COLORS.MEDIUM_GREY};
    
    & > a { 
      text-decoration: none;
      color: ${COLORS.BLUE}
    }
    
    & > a:hover { 
      text-decoration: underline;
    }
    
    @supports (-webkit-touch-callout: none) {
      margin-bottom: 60px;
    }
  `,
};


type Props = {
  className?: string;
};


/**
 * CollaboratorMenu: a component that renders the contents of the side menu on project pages in the collaborator view
 */
const CollaboratorMenu: React.FC<Props> = ({ className }) => {
  const { onIe } = useBrowser();

  const project = useAppState(state => state.projectSummary);

  const projIds = useCollaborationProjIds();
  const routes  = useRoutes(projIds.participantId, projIds.projDisplayId);

  const config      = useAppState(state => state.config);
  const system      = (config?.system || '').toLowerCase();
  const defaultLogo = React.useMemo(
    () => system === 'zf'
      ? zfLogo
      : system === 'hrdq'
        ? hrdqLogo
        : system === 'talentsmart'
          ? talentSmartLogo
          : truScoreLogo // always default to TBC
    ,
    [system],
  );

  const projectLogo = React.useMemo(
    () => project?.clientImageData ? addImgEncoding(project.clientImageData) : defaultLogo,
    [project, defaultLogo],
  );

  if (!project) {
    return <div className={className} />;
  }

  return (
    <LocalDiv css={[FLEX_COLUMN, styles.root]} className={className}>
      {
        /* Project Logo */
        onIe
          ? (
            <div css={[styles.logo]}>
              <img src={projectLogo} alt='Project Logo' />
            </div>
          )
          : (
            <img src={projectLogo} alt='Project Logo' css={[styles.logo]} />
          )
      }

      {/* Links */}
      <CollaboratorProjectSteps routes={routes} />

      {/* Spacer */}
      <Spacer sizePx={30} css={styles.spacer} />

      {/* Options */}
      <CollaboratorAppActions
        css={styles.appActions}
        customerServiceUrl={routes.CollaboratorCustomerService}
      />

      {/* Footer: Copyright/Privacy Policy  */}
      <Msg defaultMsg='' str={Str.FooterCopyrightPrivacy} css={styles.footer} />
    </LocalDiv>
  );
};

export default CollaboratorMenu;
